// src/scenes/transactions/GroupTransactions.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme, useMediaQuery,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import { gql, useQuery } from "@apollo/client";
import TransactionActionsMenu from "components/TransactionActionsMenu";
import TransactionBreakdownForm from "./TransactionBreakdownForm";
import { formatKes, getTransactionTypeMeta } from "./transactionTableUtils";

const GET_GROUP_TRANSACTIONS = gql`
  query {
    accountTransactions {
      id
      senderName
      amount
      approved
      reviewedBy
      transactionDateTime
      transactionReference
      confirmationMessage
      transactionTypeId
    }
  }
`;

const GET_GROUP_CASH_BALANCE = gql`
  query {
    groupCashBalance {
      balance
      groupId
      updatedAt
    }
  }
`;

const GET_BREAKDOWNS_BY_TRANSACTION = gql`
  query AllocationQueueByTransaction($transactionId: Int!) {
    allocationQueueByTransaction(transactionId: $transactionId) {
      id
      accountTransactionId
      scheduleType
      scheduleTypeId
      scheduleId
      allocatedAmount
      status
      processingErrorMessage
      createdDate
      processedDate
    }
  }
`;

const GroupTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [selectedBreakdowns, setSelectedBreakdowns] = useState([]);

  const {
    data: txData,
    loading: txLoading,
    error: txError,
    refetch: refetchTx,
  } = useQuery(GET_GROUP_TRANSACTIONS);

  const {
    data: balData,
    loading: balLoading,
    error: balError,
    refetch: refetchBal,
  } = useQuery(GET_GROUP_CASH_BALANCE);

  const { refetch: refetchBreakdowns } = useQuery(GET_BREAKDOWNS_BY_TRANSACTION, {
    variables: { transactionId: 0 },
    skip: true,
    fetchPolicy: "network-only",
  });

  const balance = balData?.groupCashBalance?.balance ?? 0;
  const updatedAt = balData?.groupCashBalance?.updatedAt;

  const rows = useMemo(
    () =>
      (txData?.accountTransactions || []).map((transaction, index) => ({
        ...transaction,
        sequence: index + 1,
      })),
    [txData]
  );

  const openBreakdownModal = async (tx) => {
    try {
      setSelectedTx(tx);

      const res = await refetchBreakdowns({ transactionId: tx.id });
      const breakdownRows = res?.data?.allocationQueueByTransaction || [];

      setSelectedBreakdowns(
        breakdownRows.map((row) => ({
          scheduleType: row.scheduleType,
          scheduleTypeId: row.scheduleTypeId,
          scheduleId: row.scheduleId,
          allocatedAmount: row.allocatedAmount,
          status: row.status,
          processingErrorMessage: row.processingErrorMessage,
        }))
      );

      setBreakdownOpen(true);
    } catch (error) {
      console.error("Failed to load breakdowns:", error);
      alert("Failed to load breakdowns for this transaction.");
    }
  };

  const columns = [
    {
      field: "sequence",
      headerName: "#",
      flex: 0.25,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    { field: "senderName", headerName: "From", flex: 1 },
    {
      field: "transactionTypeId",
      headerName: "Type",
      flex: 0.7,
      renderCell: ({ value }) => {
        const typeMeta = getTransactionTypeMeta(value);

        return (
          <Chip
            label={typeMeta.label}
            size="small"
            sx={{
              fontWeight: 700,
              ...typeMeta.sx,
            }}
          />
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.6,
      valueFormatter: ({ value }) => formatKes(value),
    },
    {
      field: "transactionDateTime",
      headerName: "Date",
      flex: 1,
      valueFormatter: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      field: "approved",
      headerName: "Confirmed",
      flex: 0.6,
      renderCell: ({ value }) => (
        <Typography fontWeight={600} color={value ? "success.main" : "warning.main"}>
          {value ? "Confirmed" : "Pending"}
        </Typography>
      ),
    },
    { field: "reviewedBy", headerName: "Reviewed By", flex: 0.8 },
    {
      field: "transactionReference",
      headerName: "Reference",
      flex: 1.3,
      renderCell: ({ row, value }) => {
        const hasConfirmationMessage = Boolean(row.confirmationMessage?.trim());

        const referenceContent = (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value || "-"}
            </Typography>
            {hasConfirmationMessage ? (
              <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.palette.secondary[200] }} />
            ) : null}
          </Stack>
        );

        if (!hasConfirmationMessage) {
          return referenceContent;
        }

        return (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ p: 0.5, maxWidth: 360 }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
                  Confirmation message
                </Typography>
                <Typography variant="body2">{row.confirmationMessage}</Typography>
              </Box>
            }
          >
            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                cursor: "help",
              }}
            >
              {referenceContent}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TransactionActionsMenu
          transaction={row}
          onViewBreakdown={openBreakdownModal}
          showViewButton
          hideViewInMenu
          onActionComplete={async () => {
            await refetchTx();
            await refetchBal();
          }}
        />
      ),
    },
  ];

  if (txError) return <p>Error: {txError.message}</p>;
  if (balError) return <p>Error: {balError.message}</p>;

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="GROUP TRANSACTIONS" subtitle="All Chamaa Account Activities" />

      <Box
        mt="16px"
        mb="16px"
        p="14px 18px"
        borderRadius="12px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: theme.palette.background.alt,
          border: `1px solid ${theme.palette.primary.light}`,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.secondary[200] }}
          >
            Cash Position (Confirmed)
          </Typography>

          <Typography
            variant="h4"
            sx={{ color: theme.palette.secondary[100], fontWeight: 700 }}
          >
            {balLoading ? "Loading..." : formatKes(balance)}
          </Typography>
        </Box>

        <Box textAlign="right">
          <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
            Last updated
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[100] }}>
            {updatedAt ? new Date(updatedAt).toLocaleString() : "-"}
          </Typography>
        </Box>
      </Box>

      <Box
        mt="10px"
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={txLoading}
          rows={rows}
          getRowId={(row) => row.id}
          columns={columns}
        />
      </Box>

      <TransactionBreakdownForm
        open={breakdownOpen}
        mode="view"
        initialBreakdowns={selectedBreakdowns}
        transaction={selectedTx}
        onClose={() => {
          setBreakdownOpen(false);
          setSelectedTx(null);
          setSelectedBreakdowns([]);
        }}
        onSubmit={() => {}}
        loading={false}
      
          columnVisibilityModel={
            isMobile
              ? {
                reviewedBy: false,
                transactionReference: false,
                transactionDateTime: false,
              }
              : undefined
          }/>
    </Box>
  );
};

export default GroupTransactions;
